<template>
  <div class="gesp-code-detail">
    <div class="gesp-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <h1 class="page-title">编程题</h1>
      </div>
      <div class="header-right">
        <div v-if="showTimerBar" class="timer-bar">
          <i class="el-icon-time"></i>
          <span>{{ formattedElapsed }}</span>
        </div>
        <el-button type="text" @click="prev">上一题</el-button>
        <el-button type="text" @click="next">下一题</el-button>
      </div>
    </div>

    <div class="gesp-body">
      <div class="left-panel">
        <div class="panel-title">题目总览</div>

        <div class="type-menu">
          <div class="type-item active">
            <span class="type-name">编程题</span>
            <span class="type-count">{{ activeNo }}/{{ questions.length }}</span>
          </div>
        </div>

        <div class="number-panel">
          <div class="number-title">编程题</div>
          <div class="number-grid">
            <div
              v-for="(q, idx) in questions"
              :key="q.id"
              class="num"
              :class="{ active: idx + 1 === activeNo, completed: !!q.hasSubmitted }"
              @click="selectNo(idx + 1)"
            >
              {{ idx + 1 }}
            </div>
          </div>
        </div>
      </div>

      <div class="right-panel">
        <div v-if="loadingQuestion" style="padding: 40px; text-align: center; color: #999;">加载中...</div>
        <div class="split" v-else>
          <div class="problem-card">
            <div class="problem-header">
              <div class="problem-title">
                <span class="code">{{ current.number }}</span>
                <span class="title">{{ current.title }}</span>
              </div>
              <div class="problem-meta">
                <el-tag v-if="current.difficulty" size="mini">Level {{ current.difficulty }}</el-tag>
              </div>
            </div>

            <div class="problem-content">
              <div class="section" v-if="current.content">
                <div class="section-title">题目描述</div>
                <div class="section-body rich-text" v-html="renderRichText(current.content)"></div>
              </div>

              <div class="section" v-if="current.inputDescription">
                <div class="section-title">输入描述</div>
                <div class="section-body rich-text" v-html="renderRichText(current.inputDescription)"></div>
              </div>

              <div class="section" v-if="current.outputDescription">
                <div class="section-title">输出描述</div>
                <div class="section-body rich-text" v-html="renderRichText(current.outputDescription)"></div>
              </div>

              <div class="section" v-if="current.inputFormat">
                <div class="section-title">输入样例</div>
                <div class="section-body mono rich-text" v-html="renderRichText(cleanSampleText(current.inputFormat))"></div>
              </div>

              <div class="section" v-if="current.outputFormat">
                <div class="section-title">输出样例</div>
                <div class="section-body mono rich-text" v-html="renderRichText(cleanSampleText(current.outputFormat))"></div>
              </div>

              <div class="section" v-if="current.dataRange">
                <div class="section-title">数据范围</div>
                <div class="section-body rich-text" v-html="renderRichText(current.dataRange)"></div>
              </div>

              <div class="section" v-if="current.timeLimit || current.memoryLimit">
                <div class="section-title">限制</div>
                <div class="section-body">时间限制 {{ current.timeLimit || 1000 }} ms，内存限制 {{ current.memoryLimit || 256 }} MB</div>
              </div>

              <div class="section">
                <div class="section-title">上传代码文件</div>
                <el-upload
                  class="upload-demo"
                  drag
                  action="#"
                  :auto-upload="false"
                  :on-change="handleFileChange"
                  :file-list="fileList"
                  :limit="1"
                  accept=".cpp,.c"
                >
                  <i class="el-icon-upload"></i>
                  <div class="el-upload__text">将 C/C++ 文件拖到此处，或<em>点击上传</em></div>
                  <div class="el-upload__tip" slot="tip">只能上传 .cpp/.c 文件</div>
                </el-upload>

                <div v-if="uploadedFile" class="file-info">
                  <div class="file-info-header">
                    <i class="el-icon-document"></i>
                    <span>{{ uploadedFile.name }}</span>
                    <el-button type="text" size="mini" @click="clearFile" style="margin-left: auto;">
                      <i class="el-icon-close"></i>
                    </el-button>
                  </div>
                  <div class="file-preview">
                    <pre>{{ fileContent }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="editor-card">
            <div class="editor-toolbar">
              <el-select v-model="lang" size="mini" style="width: 120px">
                <el-option label="C++ (g++)" value="cpp" />
                <el-option label="C" value="c" />
                <el-option label="Python" value="python" />
              </el-select>
              <div class="toolbar-right">
                <el-button size="mini" type="primary" @click="run" :loading="judging">运行</el-button>
                <el-button size="mini" type="success" @click="submit" :loading="judging">提交</el-button>
              </div>
            </div>

            <div class="editor-placeholder">
              <div class="placeholder-title">代码编辑器</div>
              <div class="placeholder-desc">支持语法高亮、前缀补全、Tab 缩进与括号自动补全</div>
              <div class="code-editor-shell">
                <textarea
                  ref="codeTextarea"
                  class="code-input-layer"
                  :value="code"
                  :placeholder="getEditorPlaceholder()"
                  spellcheck="false"
                  autocapitalize="off"
                  autocomplete="off"
                  autocorrect="off"
                  @input="handleEditorInput($event.target.value)"
                  @keydown="handleEditorKeydown"
                  @click="updateCompletionState"
                  @keyup="updateCompletionState"
                  @blur="closeCompletions"
                ></textarea>
                <div
                  v-if="showCompletions"
                  class="completion-panel"
                  :style="{ top: completionPosition.top + 'px', left: completionPosition.left + 'px' }"
                >
                  <div
                    v-for="(item, index) in completionItems"
                    :key="item + '-' + index"
                    class="completion-item"
                    :class="{ active: index === completionActiveIndex }"
                    @mousedown.prevent="selectCompletion(index)"
                  >
                    {{ item }}
                  </div>
                </div>
              </div>
            </div>

            <div class="output-placeholder">
              <div class="output-title">测试样例</div>
              <div class="output-body" v-if="!judgeResult">
                <div class="test-case" v-for="(testCase, index) in visibleTestCases" :key="index">
                  <div class="test-case-header">测试样例 {{ index + 1 }}</div>
                  <div class="test-case-content">
                    <div class="sample-field">
                      <div class="sample-label">输入:</div>
                      <pre class="sample-pre">{{ formatMultilineText(testCase.input) }}</pre>
                    </div>
                    <div class="sample-field">
                      <div class="sample-label">期望输出:</div>
                      <pre class="sample-pre">{{ formatMultilineText(testCase.expected_output) }}</pre>
                    </div>
                  </div>
                </div>
              </div>
              <div class="output-body" v-else>
                <div class="judge-result" :class="judgeResult.allPassed ? 'success' : 'error'">
                  <div class="result-header">
                    <i :class="judgeResult.compileError ? 'el-icon-warning-outline' : (judgeResult.allPassed ? 'el-icon-success' : 'el-icon-error')"></i>
                    <span>{{ judgeResult.compileError ? '编译错误' : (judgeResult.allPassed ? '全部通过' : '部分失败') }}</span>
                  </div>
                  <div v-if="judgeResult.compileError" class="compile-error-panel">
                    <div class="compile-error-title">{{ judgeResult.errorMessage || 'Compilation failed' }}</div>
                    <pre class="compile-error-pre">{{ judgeResult.errorDetails || '(无详细信息)' }}</pre>
                  </div>
                  <template v-else>
                    <div class="result-summary">通过: {{ judgeResult.passedCount }} / {{ judgeResult.totalCount }}</div>
                    <div v-if="judgeResult.summaryText" class="result-summary-detail">{{ judgeResult.summaryText }}</div>
                    <div class="test-results">
                      <div class="test-result" v-for="(result, index) in judgeResult.results" :key="index" :class="result.passed ? 'passed' : 'failed'">
                        <div class="result-title">测试样例 {{ index + 1 }} - {{ result.passed ? '通过' : '失败' }}</div>
                        <div class="result-detail">
                          <div class="sample-field">
                            <div class="sample-label">输入:</div>
                            <pre class="sample-pre">{{ formatMultilineText(result.input) }}</pre>
                          </div>
                          <div class="sample-field">
                            <div class="sample-label">期望输出:</div>
                            <pre class="sample-pre">{{ formatMultilineText(result.expected) }}</pre>
                          </div>
                          <div class="sample-field">
                            <div class="sample-label">实际输出:</div>
                            <pre class="sample-pre">{{ formatMultilineText(result.actual || '(无输出)') }}</pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { pagingGespQuestions, getGespQuestionById } from '@/api/gespQuestion'
import { getGespPaperDetail, savePaperProgress } from '@/api/mockTest'
import { addCodeSubmit, getLatestSubmitBatch } from '@/api/gespCodeSubmit'
import { getProgressByDate, upsertTodayPlan, getProgressPaging, updateProgressFinished, getSchemeByUserIdAndRepoId, updateSchemeFinishedIds } from '@/api/studyPlan'
import { getTokenInfo } from '@/utils/jwtUtils'
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'GESPCodeDetail',
  data() {
    return {
      questions: [],
      latestSubmitMap: {},
      activeNo: 1,
      loadingQuestion: false,
      elapsedSeconds: 0,
      timer: null,
      lang: 'cpp',
      code: '',
      judging: false,
      judgeResult: null,
      uploadedFile: null,
      fileContent: '',
      fileList: [],
      testCases: [],
      isSubmitMode: false,
      showCompletions: false,
      completionItems: [],
      completionActiveIndex: 0,
      completionPosition: {
        top: 0,
        left: 0
      },
      statProgressId: null
    }
  },
  computed: {
    current() {
      return this.questions[this.activeNo - 1] || {}
    },
    visibleTestCases() {
      return Array.isArray(this.testCases) ? this.testCases.slice(0, 2) : []
    },
    isPracticeMode() {
      const q = this.$route.query || {}
      return q.source === 'gesp-practice'
    },
    isMockTestMode() {
      const q = this.$route.query || {}
      return q.source === 'gesp-mock-test' && !!q.mockPaperId
    },
    showTimerBar() {
      return this.isMockTestMode || this.isPracticeMode
    },
    formattedElapsed() {
      const t = Number(this.elapsedSeconds || 0)
      const mm = String(Math.floor(t / 60)).padStart(2, '0')
      const ss = String(t % 60).padStart(2, '0')
      return `${mm}:${ss}`
    },
    editorLanguage() {
      if (this.lang === 'python') return 'python'
      if (this.lang === 'c') return 'c'
      return 'cpp'
    }
  },
  watch: {
    current: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.buildTestCases()
        }
      },
      immediate: true
    },
    lang() {
      this.closeCompletions()
    }
  },
  created() {
    this.hideSidebarAndNavbar()
    this.initPage()
  },
  beforeDestroy() {
    this.stopTimer()
    this.showSidebarAndNavbar()
  },
  methods: {
    extractLevelValue(raw) {
      const m = String(raw || '').trim().toUpperCase().match(/(?:GESP|CCF).*?(?:LEVEL)?\s*([1-8])/) 
      return m && m[1] ? Number(m[1]) : (raw != null && raw !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : null)
    },
    getPracticeRepoId() {
      const q = this.$route.query || {}
      const rid = Number(q.codeRepoId || q.repoId)
      return Number.isFinite(rid) && rid > 0 ? rid : 27
    },
    startTimer() {
      if (!this.showTimerBar) return
      this.stopTimer()
      this.timer = setInterval(() => {
        this.elapsedSeconds += 1
      }, 1000)
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    getElapsedSecondsFromAnswerDetail(answerDetail) {
      if (!answerDetail) return 0
      let detail = answerDetail
      if (typeof answerDetail === 'string') {
        try {
          detail = JSON.parse(answerDetail)
        } catch (e) {
          return 0
        }
      }
      const elapsed = detail && detail.meta ? Number(detail.meta.elapsedSeconds) : 0
      return Number.isFinite(elapsed) ? elapsed : 0
    },
    applyMockPaperCodeStatus(answerDetail) {
      if (!answerDetail || !Array.isArray(this.questions) || this.questions.length === 0) return
      let detail = answerDetail
      if (typeof answerDetail === 'string') {
        try {
          detail = JSON.parse(answerDetail)
        } catch (e) {
          detail = null
        }
      }
      const codeMap = detail && detail.code ? detail.code : null
      if (!codeMap) return
      this.questions = (this.questions || []).map((q, idx) => {
        const item = codeMap[String(idx + 1)] || codeMap[idx + 1]
        if (!item) return q
        return {
          ...q,
          hasSubmitted: true,
          latestSubmitStatus: item.status || (item.isCorrect ? 'AC' : 'WA')
        }
      })
    },
    async syncMockPaperProgress(isCorrect) {
      const q = this.$route.query || {}
      const mockPaperId = q.mockPaperId ? Number(q.mockPaperId) : null
      if (!mockPaperId || !this.current || !this.current.id) return
      try {
        const paperResp = await getGespPaperDetail(mockPaperId)
        const paperData = paperResp?.data?.data || paperResp?.data || {}
        const paper = paperData.paper || {}
        let detail = {}
        if (paper.answerDetail) {
          try {
            detail = typeof paper.answerDetail === 'string' ? JSON.parse(paper.answerDetail) : (paper.answerDetail || {})
          } catch (e) {
            detail = {}
          }
        }
        if (!detail || typeof detail !== 'object') detail = {}
        detail.judge = detail.judge || {}
        detail.choice = detail.choice || {}
        detail.code = detail.code || {}
        detail.meta = detail.meta || {}
        const currentIndex = this.activeNo
        const currentSubmit = this.latestSubmitMap[String(this.current.id)] || this.latestSubmitMap[this.current.id] || {}
        const passedCount = Number(currentSubmit.passedCount != null ? currentSubmit.passedCount : (this.judgeResult ? this.judgeResult.passedCount : 0)) || 0
        const totalCount = Number(currentSubmit.totalCount != null ? currentSubmit.totalCount : (this.judgeResult ? this.judgeResult.totalCount : this.testCases.length)) || 0
        detail.code[currentIndex] = {
          questionId: this.current.id,
          status: isCorrect ? 'AC' : 'WA',
          isCorrect: !!isCorrect,
          passedCount,
          totalCount
        }
        detail.meta.elapsedSeconds = Number(this.elapsedSeconds || 0)
        const judgeCorrect = Object.values(detail.judge || {}).filter(item => item && item.isCorrect === true).length
        const choiceCorrect = Object.values(detail.choice || {}).filter(item => item && item.isCorrect === true).length
        const codePassedCount = Object.values(detail.code || {}).reduce((sum, item) => {
          return sum + (Number(item && item.passedCount) || 0)
        }, 0)
        await savePaperProgress(mockPaperId, {
          score: (judgeCorrect + choiceCorrect + codePassedCount) * 5,
          correctCount: judgeCorrect + choiceCorrect + codePassedCount,
          answerDetail: JSON.stringify(detail)
        })
      } catch (e) {
        console.warn('同步模拟卷编程题进度失败:', e)
      }
    },
    async syncMockPaperElapsedOnly() {
      const q = this.$route.query || {}
      const mockPaperId = q.mockPaperId ? Number(q.mockPaperId) : null
      if (!mockPaperId) return
      try {
        const paperResp = await getGespPaperDetail(mockPaperId)
        const paperData = paperResp?.data?.data || paperResp?.data || {}
        const paper = paperData.paper || {}
        let detail = {}
        if (paper.answerDetail) {
          try {
            detail = typeof paper.answerDetail === 'string' ? JSON.parse(paper.answerDetail) : (paper.answerDetail || {})
          } catch (e) {
            detail = {}
          }
        }
        detail = detail && typeof detail === 'object' ? detail : {}
        detail.judge = detail.judge || {}
        detail.choice = detail.choice || {}
        detail.code = detail.code || {}
        detail.meta = detail.meta || {}
        detail.meta.elapsedSeconds = Number(this.elapsedSeconds || 0)
        const judgeCorrect = Object.values(detail.judge || {}).filter(item => item && item.isCorrect === true).length
        const choiceCorrect = Object.values(detail.choice || {}).filter(item => item && item.isCorrect === true).length
        const codePassedCount = Object.values(detail.code || {}).reduce((sum, item) => sum + (Number(item && item.passedCount) || 0), 0)
        await savePaperProgress(mockPaperId, {
          score: (judgeCorrect + choiceCorrect + codePassedCount) * 5,
          correctCount: judgeCorrect + choiceCorrect + codePassedCount,
          answerDetail: JSON.stringify(detail)
        })
      } catch (e) {
        console.warn('同步模拟卷耗时失败:', e)
      }
    },
    getTodayDate() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    async syncStudyProgress(questionId, isCorrect) {
      try {
        const user = getTokenInfo && getTokenInfo()
        const userId = user?.id
        const repoIdVal = Number(this.$route?.query?.repoId) || Number(this.current?.repoId) || 27
        const qid = Number(questionId)
        if (!userId || !repoIdVal || !qid) return

        try {
          const schemeResp = await getSchemeByUserIdAndRepoId(userId, repoIdVal)
          const scheme = schemeResp?.data
          const schemeId = scheme ? (scheme.id ?? scheme.schemeId) : null
          if (schemeId) {
            const currentFinished = String(scheme?.finishedId || scheme?.finished_id || '')
            const finishedSet = new Set(currentFinished.split(/[，,;\s]+/).filter(x => x).map(Number))
            finishedSet.add(qid)
            const merged = Array.from(finishedSet).filter(id => !isNaN(id) && id > 0).join(',')
            await updateSchemeFinishedIds(schemeId, merged)
          }
        } catch (schemeErr) {
          console.error('同步学习方案失败:', schemeErr)
        }

        const todayStr = this.getTodayDate()
        let progressId = this.statProgressId
        if (!progressId) {
          const idRes = await getProgressByDate(userId, repoIdVal, todayStr)
          const idData = idRes?.data?.data ?? idRes?.data ?? idRes
          progressId = (typeof idData === 'number' || typeof idData === 'string') ? Number(idData) : (idData && idData.id)
        }
        if (!progressId) {
          const planRes = await upsertTodayPlan({ userId, repoId: repoIdVal, date: todayStr })
          const planData = planRes?.data?.data ?? planRes?.data ?? planRes
          progressId = planData && (planData.id || planData.ID)
        }
        this.statProgressId = progressId || this.statProgressId
        if (!progressId) return

        const page = await getProgressPaging({ pageNum: 1, pageSize: 1, userId, repoId: repoIdVal, dateFrom: todayStr, dateTo: todayStr })
        const pdata = page?.data?.data ?? page?.data ?? {}
        const records = Array.isArray(pdata.records) ? pdata.records : []
        const first = records && records.length ? records[0] : null
        if (!first) return

        const currentFinished = String(first.finished_id || first.finishedId || '')
        const finishedSet = new Set(currentFinished.split(/[，,;\s]+/).filter(x => x).map(Number))
        finishedSet.add(qid)
        const mergedFinished = Array.from(finishedSet).filter(id => !isNaN(id) && id > 0).join(',')
        const correctCount = Number(first.correct_count ?? first.correctCount ?? 0) + (isCorrect ? 1 : 0)
        const wrongCount = Number(first.wrong_count ?? first.wrongCount ?? 0) + (isCorrect ? 0 : 1)
        const reviewDone = Number(first.review_done ?? first.reviewDone ?? 0)
        await updateProgressFinished(progressId, mergedFinished, correctCount, wrongCount, reviewDone)
      } catch (e) {
        console.error('同步学习进度失败:', e)
      }
    },
    async initPage() {
  const q = this.$route.query || {}
  const questionId = q.questionId ? Number(q.questionId) : null
  const mockPaperId = q.mockPaperId ? Number(q.mockPaperId) : null
  const year = q.year || null
  const month = q.month || null
  const level = this.extractLevelValue(q.level)
  const source = q.source || ''
  const routeElapsed = Number(q.elapsedSeconds)

  this.loadingQuestion = true
  try {
    if (questionId) {
      if (source === 'gesp-mock-test') {
        const paperResp = mockPaperId ? await getGespPaperDetail(mockPaperId) : null
        const paperData = paperResp?.data?.data || paperResp?.data || {}
        const paperCodeQuestions = Array.isArray(paperData.codeQuestions) ? paperData.codeQuestions : []
        if (paperCodeQuestions.length > 0) {
          this.questions = paperCodeQuestions
          this.applyMockPaperCodeStatus(paperData.paper && paperData.paper.answerDetail)
          this.elapsedSeconds = this.getElapsedSecondsFromAnswerDetail(paperData.paper && paperData.paper.answerDetail)
        } else {
          const singleResp = await getGespQuestionById(questionId)
          const single = singleResp?.data?.data ?? singleResp?.data ?? {}
          this.questions = single && single.id ? [single] : []
        }
        const idx = this.questions.findIndex(item => Number(item && item.id) === questionId)
        this.activeNo = idx >= 0 ? idx + 1 : 1
      } else {
        if (Number.isFinite(routeElapsed) && routeElapsed >= 0) {
          this.elapsedSeconds = routeElapsed
        }
        const singleResp = await getGespQuestionById(questionId)
        const single = singleResp?.data?.data ?? singleResp?.data ?? {}
        const practiceRepoId = this.getPracticeRepoId()
        const listResp = await pagingGespQuestions({
          pageNum: 1,
          pageSize: 200,
          repoId: practiceRepoId,
          year: year ? String(year) : undefined,
          month: month ? String(month) : undefined,
          level: level || undefined
        })
        const raw = listResp?.data?.data ?? listResp?.data ?? {}
        const records = Array.isArray(raw?.records) ? raw.records
          : (Array.isArray(raw?.list) ? raw.list : (Array.isArray(raw) ? raw : []))
        this.questions = records.length > 0 ? records : (single && single.id ? [single] : [])
        const idx = this.questions.findIndex(item => Number(item && item.id) === questionId)
        this.activeNo = idx >= 0 ? idx + 1 : 1
      }
    } else {
      if (Number.isFinite(routeElapsed) && routeElapsed >= 0) {
        this.elapsedSeconds = routeElapsed
      }
      const practiceRepoId = this.getPracticeRepoId()
      const listResp = await pagingGespQuestions({
        pageNum: 1,
        pageSize: 200,
        repoId: practiceRepoId,
        year: year ? String(year) : undefined,
        month: month ? String(month) : undefined,
        level: level || undefined
      })
      const raw = listResp?.data?.data ?? listResp?.data ?? {}
      this.questions = Array.isArray(raw?.records) ? raw.records
        : (Array.isArray(raw?.list) ? raw.list : (Array.isArray(raw) ? raw : []))
      this.activeNo = 1
    }
    await this.loadSubmitStatus()
    this.startTimer()
  } catch (e) {
    console.error('加载编程题失败:', e)
    this.$message.error('加载失败')
  } finally {
    this.loadingQuestion = false
  }
},
    hideSidebarAndNavbar() {
      this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) sidebar.style.display = 'none'
      if (navbar) navbar.style.display = 'none'

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = '0'
        appMain.style.margin = '0'
      }
    },
    showSidebarAndNavbar() {
      this.$store.dispatch('app/openSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) sidebar.style.display = ''
      if (navbar) navbar.style.display = ''

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = ''
        appMain.style.margin = ''
      }
    },
    selectNo(n) {
      this.activeNo = n
      this.judgeResult = null
      this.uploadedFile = null
      this.fileContent = ''
      this.fileList = []
      this.closeCompletions()
      this.restoreEditorFromLatestSubmit()
    },
    normalizeSubmitLang(lang) {
      const raw = String(lang || '').toLowerCase()
      if (raw === 'python' || raw === 'py' || raw === 'python3') return 'python'
      if (raw === 'c') return 'c'
      return 'cpp'
    },
    restoreEditorFromLatestSubmit() {
      const current = this.current
      if (!current || !current.id) {
        this.code = ''
        this.judgeResult = null
        return
      }
      const submit = this.latestSubmitMap[String(current.id)] || this.latestSubmitMap[current.id]
      if (submit && submit.code) {
        this.code = submit.code
        this.lang = this.normalizeSubmitLang(submit.lang)
        this.restoreJudgeResultFromLatestSubmit(submit)
      } else {
        this.code = ''
        this.judgeResult = null
      }
    },
    restoreJudgeResultFromLatestSubmit(submit) {
      if (!submit) {
        this.judgeResult = null
        return
      }
      const passedCount = Number(submit.passedCount) || 0
      const totalCount = Number(submit.totalCount) || 0
      const status = String(submit.status || '').toUpperCase()
      const allPassed = status === 'AC' || (totalCount > 0 && passedCount === totalCount)
      this.judgeResult = {
        compileError: false,
        allPassed,
        passedCount,
        totalCount,
        errorMessage: '',
        errorDetails: '',
        summaryText: allPassed ? '上次提交已通过全部测试样例。' : '以下为上次提交的判题结果摘要。',
        results: []
      }
    },
    getEditorPlaceholder() {
      if (this.lang === 'python') {
        return 'def solve():\n    pass\n\nif __name__ == "__main__":\n    solve()'
      }
      if (this.lang === 'c') {
        return '#include <stdio.h>\n\nint main() {\n    return 0;\n}'
      }
      return '#include <bits/stdc++.h>\nusing namespace std;\n\nint main() {\n    return 0;\n}'
    },
    handleEditorInput(value) {
      this.code = value
      this.$nextTick(() => {
        this.updateCompletionState()
      })
    },
    handleEditorKeydown(event) {
      const textarea = event && event.target
      if (!textarea) return
      if (this.showCompletions && event.key === 'ArrowDown') {
        event.preventDefault()
        if (this.completionItems.length) {
          this.completionActiveIndex = (this.completionActiveIndex + 1) % this.completionItems.length
        }
        return
      }
      if (this.showCompletions && event.key === 'ArrowUp') {
        event.preventDefault()
        if (this.completionItems.length) {
          this.completionActiveIndex = (this.completionActiveIndex - 1 + this.completionItems.length) % this.completionItems.length
        }
        return
      }
      if (this.showCompletions && (event.key === 'Enter' || event.key === 'Tab')) {
        const picked = this.completionItems[this.completionActiveIndex]
        if (picked) {
          event.preventDefault()
          this.applyCompletion(picked)
          return
        }
      }
      if (event.key === 'Escape') {
        this.closeCompletions()
        return
      }
      if (event.key === 'Tab') {
        event.preventDefault()
        this.insertEditorText('    ')
        return
      }
      const pairs = {
        '(': ')',
        '[': ']',
        '{': '}',
        '"': '"',
        "'": "'"
      }
      if (pairs[event.key]) {
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const selected = this.code.slice(start, end)
        event.preventDefault()
        this.code = `${this.code.slice(0, start)}${event.key}${selected}${pairs[event.key]}${this.code.slice(end)}`
        this.$nextTick(() => {
          const pos = start + 1
          textarea.focus()
          textarea.setSelectionRange(pos, pos + selected.length)
          this.updateCompletionState()
        })
      }
    },
    insertEditorText(text) {
      const textarea = this.$refs.codeTextarea
      if (!textarea) return
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      this.code = `${this.code.slice(0, start)}${text}${this.code.slice(end)}`
      this.$nextTick(() => {
        const cursor = start + text.length
        textarea.focus()
        textarea.setSelectionRange(cursor, cursor)
        this.updateCompletionState()
      })
    },
    getCompletionDictionary() {
      const common = ['for', 'while', 'if', 'else', 'break', 'continue', 'return']
      if (this.lang === 'python') {
        return ['def', 'import', 'from', 'print', 'input', 'range', 'len', 'list', 'dict', 'int', 'str', 'float', 'enumerate', 'map', 'solve']
          .concat(common)
      }
      if (this.lang === 'c') {
        return ['#include', 'int', 'long long', 'printf', 'scanf', 'main', 'return', 'malloc', 'free', 'strlen']
          .concat(common)
      }
      return ['#include', 'using namespace std;', 'int', 'long long', 'vector', 'string', 'cin', 'cout', 'endl', 'main', 'sort', 'max', 'min', 'solve']
        .concat(common)
    },
    getCurrentWordInfo() {
      const textarea = this.$refs.codeTextarea
      const position = textarea ? textarea.selectionStart : this.code.length
      const before = this.code.slice(0, position)
      const matched = before.match(/[A-Za-z_#][A-Za-z0-9_:#<>-]*$/)
      if (!matched) {
        return { word: '', start: position, end: position }
      }
      return {
        word: matched[0],
        start: position - matched[0].length,
        end: position
      }
    },
    updateCompletionState() {
      const textarea = this.$refs.codeTextarea
      if (!textarea) return
      const info = this.getCurrentWordInfo()
      if (!info.word || info.word.length < 1) {
        this.closeCompletions()
        return
      }
      const lowerWord = info.word.toLowerCase()
      const items = this.getCompletionDictionary()
        .filter(item => String(item).toLowerCase().startsWith(lowerWord) && String(item).toLowerCase() !== lowerWord)
        .slice(0, 8)
      if (!items.length) {
        this.closeCompletions()
        return
      }
      this.completionItems = items
      this.completionActiveIndex = 0
      this.showCompletions = true
      this.updateCompletionPosition()
    },
    updateCompletionPosition() {
      const textarea = this.$refs.codeTextarea
      if (!textarea) return
      const lineHeight = 22
      const paddingTop = 14
      const paddingLeft = 12
      const cursor = textarea.selectionStart || 0
      const before = this.code.slice(0, cursor)
      const lines = before.split('\n')
      const line = lines.length - 1
      const column = (lines[lines.length - 1] || '').length
      this.completionPosition = {
        top: paddingTop + (line + 1) * lineHeight - textarea.scrollTop,
        left: paddingLeft + Math.min(column * 8.2, textarea.clientWidth - 180) - textarea.scrollLeft
      }
    },
    applyCompletion(text) {
      const textarea = this.$refs.codeTextarea
      if (!textarea) return
      const info = this.getCurrentWordInfo()
      this.code = `${this.code.slice(0, info.start)}${text}${this.code.slice(info.end)}`
      this.$nextTick(() => {
        const cursor = info.start + text.length
        textarea.focus()
        textarea.setSelectionRange(cursor, cursor)
        this.closeCompletions()
      })
    },
    closeCompletions() {
      this.showCompletions = false
      this.completionItems = []
      this.completionActiveIndex = 0
    },
    buildTestCases() {
      this.testCases = []
      if (!this.current) return

      if (this.current.testCases) {
        try {
          const parsed = JSON.parse(this.current.testCases)
          if (Array.isArray(parsed) && parsed.length > 0) {
            this.testCases = parsed.map(tc => ({
              input: this.decodeEscapedNewlines(tc.input),
              expected_output: this.decodeEscapedNewlines(tc.output || tc.expected_output)
            }))
            return
          }
        } catch (e) {
          console.error('解析测试用例失败:', e)
        }
      }

      const inputStr = this.current.inputFormat || ''
      const outputStr = this.current.outputFormat || ''

      if (!inputStr || !outputStr) return

      const normalizedInput = this.decodeEscapedNewlines(inputStr)
      const normalizedOutput = this.decodeEscapedNewlines(outputStr)
      const inputs = normalizedInput.trim().split('\n').filter(s => s.trim())
      const outputs = normalizedOutput.trim().split('\n').filter(s => s.trim())

      const count = Math.min(inputs.length, outputs.length)
      for (let i = 0; i < count; i++) {
        const cleanInput = inputs[i].trim().replace(/^样例\s*\d+\s*[：:]\s*/, '')
        const cleanOutput = outputs[i].trim().replace(/^样例\s*\d+\s*[：:]\s*/, '')

        this.testCases.push({
          input: cleanInput,
          expected_output: cleanOutput
        })
      }
    },
    prev() {
      if (this.activeNo <= 1) return
      this.activeNo -= 1
      this.judgeResult = null
      this.uploadedFile = null
      this.fileContent = ''
      this.fileList = []
      this.closeCompletions()
      this.restoreEditorFromLatestSubmit()
    },
    next() {
      if (this.activeNo >= this.questions.length) return
      this.activeNo += 1
      this.judgeResult = null
      this.uploadedFile = null
      this.fileContent = ''
      this.fileList = []
      this.closeCompletions()
      this.restoreEditorFromLatestSubmit()
    },
    async loadSubmitStatus() {
      try {
        const user = getTokenInfo && getTokenInfo()
        const userId = user && user.id
        const ids = Array.isArray(this.questions) ? this.questions.map(q => q && q.id).filter(Boolean) : []
        if (!userId || ids.length === 0) return

        const resp = await getLatestSubmitBatch(userId, ids)
        const map = resp?.data?.data ?? resp?.data ?? {}
        this.latestSubmitMap = map || {}
        this.questions = (this.questions || []).map(q => {
          const latest = map ? (map[String(q.id)] || map[q.id]) : null
          return {
            ...q,
            hasSubmitted: !!(q && q.hasSubmitted) || !!latest,
            latestSubmitStatus: latest && latest.status ? String(latest.status).toUpperCase() : (q && q.latestSubmitStatus ? q.latestSubmitStatus : '')
          }
        })
        this.restoreEditorFromLatestSubmit()
      } catch (e) {
        console.error('加载编程题提交状态失败:', e)
      }
    },
    async run() {
      this.isSubmitMode = false
      await this.judgeCode()
    },
    async submit() {
      this.isSubmitMode = true
      await this.judgeCode()
    },
    handleFileChange(file, fileList) {
      if (fileList.length > 0) {
        this.uploadedFile = file.raw
        this.fileList = [file]

        const reader = new FileReader()
        reader.onload = (e) => {
          this.fileContent = e.target.result
        }
        reader.readAsText(file.raw)
      }
    },
    clearFile() {
      this.uploadedFile = null
      this.fileContent = ''
      this.fileList = []
      this.judgeResult = null
    },
    async judgeCode() {
      if (!this.uploadedFile && !this.code.trim()) {
        this.$message.warning('请先上传代码文件或输入代码')
        return
      }

      this.judging = true
      this.judgeResult = null

      try {
        const formData = new FormData()

        if (this.uploadedFile) {
          formData.append('code_file', this.uploadedFile)
        } else {
          const codeBlob = new Blob([this.code], { type: 'text/plain' })
          formData.append('code_file', codeBlob, 'solution.cpp')
        }

        // 用题目的 inputFormat/outputFormat 作为测试用例（如果有的话）
        const tc = this.testCases.length > 0 ? this.testCases : []
        formData.append('test_cases', JSON.stringify(tc))

        const response = await fetch('http://123.56.176.195:7777/run_test_cases/', {
          method: 'POST',
          body: formData
        })

        const responseText = await response.text()
        let result = null
        try {
          result = responseText ? JSON.parse(responseText) : null
        } catch (e) {
          result = null
        }

        if (!response.ok && !(result && (result.error || result.details))) {
          throw new Error(responseText || `HTTP error! status: ${response.status}`)
        }

        if (result && (result.error || result.details)) {
          this.judgeResult = {
            compileError: true,
            allPassed: false,
            passedCount: 0,
            totalCount: this.testCases.length,
            results: [],
            errorMessage: result.error || 'Compilation failed',
            errorDetails: this.decodeEscapedNewlines(result.details || ''),
            summaryText: ''
          }
          this.$message.error(this.judgeResult.errorMessage)
          if (this.isSubmitMode) {
            await this.saveQuestionRecord(false)
          }
          return
        }

        this.judgeResult = {
          compileError: false,
          allPassed: result.all_passed || false,
          passedCount: result.passed_count || 0,
          totalCount: result.total_count || this.testCases.length,
          errorMessage: '',
          errorDetails: '',
          summaryText: result.all_passed ? '代码已通过全部测试样例。' : '',
          results: (result.results || []).map((r, index) => ({
            passed: r.passed || false,
            input: this.decodeEscapedNewlines(this.testCases[index] ? this.testCases[index].input : ''),
            expected: this.decodeEscapedNewlines(this.testCases[index] ? this.testCases[index].expected_output : ''),
            actual: this.decodeEscapedNewlines(r.actual_output || r.output || '')
          }))
        }

        if (this.judgeResult.allPassed) {
          this.$message.success('恭喜！所有测试样例通过')
          // 只有提交模式才记录做题结果
          if (this.isSubmitMode) {
            await this.saveQuestionRecord(true)
          }
        } else {
          this.$message.error(`测试未通过：${this.judgeResult.passedCount}/${this.judgeResult.totalCount}`)
          // 只有提交模式才记录做题结果
          if (this.isSubmitMode) {
            await this.saveQuestionRecord(false)
          }
        }
      } catch (error) {
        console.error('判题失败:', error)
        this.$message.error('判题失败: ' + error.message)
      } finally {
        this.judging = false
      }
    },
    async saveQuestionRecord(isCorrect) {
      try {
        const user = getTokenInfo && getTokenInfo()
        const userId = user && user.id
        if (!userId || !this.current || !this.current.id) return

        const codeContent = this.uploadedFile ? this.fileContent : this.code
        const repoIdVal = Number(this.$route?.query?.codeRepoId) || Number(this.current?.repoId) || 27
        const submit = {
          userId: userId,
          quesId: this.current.id,
          repoId: repoIdVal,
          status: isCorrect ? 'AC' : 'WA',
          lang: this.lang || 'cpp',
          code: codeContent || '',
          passedCount: this.judgeResult ? this.judgeResult.passedCount : 0,
          totalCount: this.judgeResult ? this.judgeResult.totalCount : this.testCases.length,
          timeUsed: null,
          memoryUsed: null,
          errorMsg: null
        }

        await addCodeSubmit(submit)
        this.latestSubmitMap = {
          ...(this.latestSubmitMap || {}),
          [String(this.current.id)]: {
            ...(this.latestSubmitMap[String(this.current.id)] || {}),
            quesId: this.current.id,
            status: isCorrect ? 'AC' : 'WA',
            lang: this.lang || 'cpp',
            code: codeContent || '',
            passedCount: this.judgeResult ? this.judgeResult.passedCount : 0,
            totalCount: this.judgeResult ? this.judgeResult.totalCount : this.testCases.length
          }
        }
        if (this.current && this.current.id) {
          this.questions = (this.questions || []).map(q => {
            if (q.id !== this.current.id) return q
            return {
              ...q,
              hasSubmitted: true,
              latestSubmitStatus: isCorrect ? 'AC' : 'WA'
            }
          })
        }
        await this.syncMockPaperProgress(isCorrect)
        if (this.isPracticeMode) {
          await this.syncStudyProgress(this.current.id, isCorrect)
        }
      } catch (e) {
        console.error('保存提交记录失败:', e)
      }
    },
    cleanSampleText(text) {
      if (!text) return ''
      return this.decodeEscapedNewlines(text).split('\n')
        .map(line => line.trim().replace(/^样例\s*\d+\s*[：:]\s*/, ''))
        .join('\n')
    },
    decodeEscapedNewlines(text) {
      if (text == null) return ''
      return String(text)
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\n')
    },
    selectCompletion(index) {
      this.completionActiveIndex = index
      const picked = this.completionItems[index]
      if (picked) this.applyCompletion(picked)
    },
    normalizeLatexInput(text) {
      if (!text) return ''
      return String(text)
        .replace(/(^|[^\\])frac\s*\{/g, '$1\\frac{')
        .replace(/(^|[^\\])sqrt\s*\{/g, '$1\\sqrt{')
        .replace(/(^|[^\\])cdot\b/g, '$1\\cdot')
        .replace(/(^|[^\\])times\b/g, '$1\\times')
    },
    escapeHtml(text) {
      if (text == null) return ''
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    },
    decodeHtmlEntities(text) {
      if (text == null) return ''
      return String(text)
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
    },
    renderKatex(expr, displayMode) {
      try {
        let formula = this.decodeHtmlEntities(this.normalizeLatexInput(String(expr || '').trim().replace(/\\/g, '\\')))
        if (window && typeof window.fixLatexNotation === 'function') {
          formula = window.fixLatexNotation(formula)
        }
        return katex.renderToString(formula, {
          throwOnError: false,
          displayMode: !!displayMode,
          strict: false,
          trust: true
        })
      } catch (_) {
        return this.escapeHtml(displayMode ? `\\[${expr}\\]` : `\\(${expr}\\)`)
      }
    },
    renderRichText(text) {
      const decoded = this.decodeEscapedNewlines(text)
      const input = this.escapeHtml(this.normalizeLatexInput(decoded)
        .replace(/\r\n/g, '\n')
        .replace(/\\\\\(/g, '\\(')
        .replace(/\\\\\)/g, '\\)')
        .replace(/\\\\\[/g, '\\[')
        .replace(/\\\\\]/g, '\\]'))
      if (katex && typeof katex.renderToString === 'function') {
        let html = input
        html = html.replace(/\\\[([\s\S]+?)\\\]/g, (_, expr) => this.renderKatex(expr, true))
        html = html.replace(/\$\$([\s\S]+?)\$\$/g, (_, expr) => this.renderKatex(expr, true))
        html = html.replace(/\\\(([\s\S]+?)\\\)/g, (_, expr) => this.renderKatex(expr, false))
        html = html.replace(/\$([^$\n]+?)\$/g, (_, expr) => this.renderKatex(expr, false))
        return html.replace(/\n/g, '<br>')
      }
      return input.replace(/\n/g, '<br>')
    },
    normalizeMultilineText(text) {
      const decoded = this.decodeEscapedNewlines(text)
      return decoded
        .split('\n')
        .map(line => line.trim())
        .join('\n')
        .trim()
    },
    formatMultilineText(text) {
      return this.normalizeMultilineText(text)
    },
    closeCurrentWindow() {
      try {
        window.open('', '_self')
        window.close()
      } catch (e) {
      }
      return !!window.closed
    },
    goBack() {
      const q = this.$route.query || {}
      if (q.source === 'gesp-mock-test') {
        this.stopTimer()
        this.syncMockPaperElapsedOnly().finally(() => {
          this.$router.push({
            path: '/gesp-mock-test',
            query: {
              repoId: q.repoId || undefined,
              codeRepoId: q.codeRepoId || undefined,
              level: q.level || undefined,
              paperId: q.mockPaperId || undefined,
              source: 'code-detail-back'
            }
          })
        })
        return
      }
      if (q.source === 'gesp-code-list') {
        this.stopTimer()
        this.$router.push({
          path: '/gesp-code-list',
          query: {
            ...q,
            level: q.level || undefined,
            courseTypeCode: q.courseTypeCode || 'GESP',
            repoId: q.repoId || undefined,
            source: 'code-detail-back'
          }
        })
        return
      }
      if (q.source === 'gesp-practice') {
        this.stopTimer()
        this.$router.push({
          path: '/gesp-practice',
          query: {
            year: q.year || undefined,
            month: q.month || undefined,
            level: q.level || undefined,
            repoId: q.repoId || undefined,
            elapsedSeconds: this.elapsedSeconds || 0,
            source: 'code-detail-back'
          }
        })
        return
      }
      this.stopTimer()
      const closed = this.closeCurrentWindow()
      if (!closed) {
        this.$router.go(-1)
      }
    }
  }
}
</script>

<style scoped>
.gesp-code-detail { min-height: 100vh; background: #f5f7fa; }
.gesp-header { background: #fff; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 8px rgba(0,0,0,0.06); position: sticky; top: 0; z-index: 10; }
.back-btn { font-size: 18px; color: #666; cursor: pointer; padding: 4px; border-radius: 50%; }
.back-btn:hover { background: #f5f5f5; }
.page-title { margin: 0; font-size: 16px; font-weight: 600; }
.header-left { display: flex; align-items: center; gap: 10px; }
.header-right { display: flex; align-items: center; gap: 10px; }
.timer-bar { display: flex; align-items: center; gap: 6px; color: #409EFF; font-weight: 600; }

.gesp-body { display: flex; gap: 12px; padding: 12px; height: calc(100vh - 48px); }
.left-panel { width: 240px; background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 12px; height: 100%; overflow-y: auto; display: flex; flex-direction: column; }
.right-panel { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 12px; height: 100%; }
.panel-title { font-weight: 600; color: #333; margin-bottom: 10px; }

.type-menu { display: flex; flex-direction: column; gap: 6px; }
.type-item { display: flex; justify-content: space-between; align-items: center; padding: 8px 10px; border-radius: 8px; cursor: pointer; color: #333; background: #ecf5ff; border: 1px solid #409EFF; }
.type-name { font-weight: 600; }
.type-count { color: #666; font-size: 12px; }

.number-panel { margin-top: 12px; flex: 1; overflow-y: auto; display: flex; flex-direction: column; }
.number-title { font-weight: 600; color: #333; margin-bottom: 8px; }
.number-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
.num { height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 6px; cursor: pointer; border: 1px solid #e5e7eb; color: #333; background: #fff; font-size: 13px; }
.num.completed { border-color: #67c23a; background: #f0f9eb; color: #2f7a1f; }
.num.active { border-color: #409EFF; background: #ecf5ff; }
.num.completed.active { border-color: #67c23a; background: #f0f9eb; color: #2f7a1f; }

.split { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; height: 100%; }
.problem-card, .editor-card { background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 14px; min-width: 0; height: 100%; overflow-y: auto; display: flex; flex-direction: column; }
.problem-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.problem-title { display: flex; align-items: center; gap: 8px; min-width: 0; }
.problem-title .code { font-weight: 700; color: #333; }
.problem-title .title { color: #333; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.problem-meta { color: #666; font-size: 12px; }

.section { margin-bottom: 12px; }
.section-title { font-weight: 600; color: #333; margin-bottom: 6px; }
.problem-content { color: #333; line-height: 1.7; flex: 1; overflow-y: auto; }
.section-body { white-space: pre-wrap; word-break: break-word; }
.section-body.rich-text { white-space: normal; }
.section-body.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; background: #f8fafc; border: 1px solid #eef2f7; border-radius: 8px; padding: 8px; white-space: pre-wrap; }

.editor-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 10px; }
.toolbar-right { display: flex; gap: 8px; }
.editor-placeholder { border: 1px solid #eef2f7; border-radius: 10px; padding: 12px; background: #f8fafc; }
.placeholder-title { font-weight: 600; color: #333; margin-bottom: 6px; }
.placeholder-desc { color: #666; margin-bottom: 10px; }
.code-editor-shell { position: relative; min-height: 396px; border: 1px solid #dbe5f0; border-radius: 8px; overflow: hidden; background: #ffffff; }
.code-input-layer { display: block; width: 100%; min-height: 396px; margin: 0; padding: 14px 12px; resize: vertical; border: 0; outline: none; background: #ffffff; color: #1f2937; font-size: 14px; line-height: 22px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; white-space: pre; tab-size: 4; overflow: auto; box-sizing: border-box; }
.code-input-layer::placeholder { color: #94a3b8; }
.completion-panel { position: absolute; z-index: 3; min-width: 170px; max-width: 240px; background: #fff; border: 1px solid #dbe5f0; border-radius: 8px; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14); overflow: hidden; }
.completion-item { padding: 8px 12px; font-size: 13px; line-height: 1.4; color: #334155; cursor: pointer; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
.completion-item.active, .completion-item:hover { background: #ecf5ff; color: #1d4ed8; }
.output-placeholder { margin-top: 12px; border: 1px solid #eef2f7; border-radius: 10px; padding: 12px; background: #fff; }
.output-title { font-weight: 600; color: #333; margin-bottom: 6px; }
.output-body.placeholder { color: #999; }

.tip-card { background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 12px; color: #666; line-height: 1.7; }

.test-case { margin-bottom: 12px; padding: 8px; background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 6px; }
.test-case-header { font-weight: 600; color: #333; margin-bottom: 6px; font-size: 13px; }
.test-case-content { font-size: 13px; color: #666; display: flex; flex-direction: column; align-items: stretch; padding-left: 0; }
.sample-field { margin-bottom: 10px; width: 100%; }
.sample-field:last-child { margin-bottom: 0; }
.sample-label { margin-bottom: 4px; font-weight: 600; color: #606266; line-height: 1.4; padding-left: 0; }
.sample-pre { margin: 0; padding: 8px 10px; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; color: #333; white-space: pre-wrap; word-break: break-word; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 12px; line-height: 1.6; text-align: left; display: block; width: 100%; box-sizing: border-box; margin-left: 0; tab-size: 2; }

.judge-result { padding: 12px; border-radius: 8px; }
.judge-result.success { background: #f0f9ff; border: 1px solid #67c23a; }
.judge-result.error { background: #fef0f0; border: 1px solid #f56c6c; }

.result-header { display: flex; align-items: center; gap: 8px; font-size: 16px; font-weight: 600; margin-bottom: 8px; }
.result-header i { font-size: 20px; }
.judge-result.success .result-header { color: #67c23a; }
.judge-result.error .result-header { color: #f56c6c; }

.result-summary { font-size: 14px; color: #666; margin-bottom: 12px; }
.result-summary-detail { font-size: 13px; color: #409EFF; margin-bottom: 12px; }

.test-results { display: flex; flex-direction: column; gap: 10px; }
.test-result { padding: 10px; border-radius: 6px; background: #fff; }
.test-result.passed { border-left: 3px solid #67c23a; }
.test-result.failed { border-left: 3px solid #f56c6c; }

.result-title { font-weight: 600; margin-bottom: 6px; font-size: 13px; }
.test-result.passed .result-title { color: #67c23a; }
.test-result.failed .result-title { color: #f56c6c; }

.result-detail { font-size: 13px; color: #666; display: flex; flex-direction: column; align-items: stretch; }
.compile-error-panel { background: #fff; border-radius: 6px; border-left: 3px solid #e6a23c; padding: 10px; }
.compile-error-title { color: #e6a23c; font-size: 14px; font-weight: 600; margin-bottom: 8px; }
.compile-error-pre { margin: 0; padding: 10px; border: 1px solid #f3d19e; border-radius: 6px; background: #fffaf3; color: #7c2d12; white-space: pre-wrap; word-break: break-word; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 12px; line-height: 1.6; }
.rich-text :deep(.katex) { font-size: 1em; }
.rich-text :deep(.katex-display) { margin: 0.35em 0; text-align: left; overflow-x: auto; overflow-y: hidden; }

.upload-demo { margin-bottom: 12px; }
.file-info { margin-top: 12px; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
.file-info-header { display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: #f8fafc; border-bottom: 1px solid #e5e7eb; font-size: 14px; font-weight: 600; color: #333; }
.file-info-header i { font-size: 16px; color: #409EFF; }
.file-preview { max-height: 300px; overflow-y: auto; background: #fff; }
.file-preview pre { margin: 0; padding: 12px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 13px; line-height: 1.6; color: #333; white-space: pre-wrap; word-wrap: break-word; }
</style>
